import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { newsData } from '../data/newsData';
import { RelatedArticles } from '../components/RelatedArticles';
import { CalendarIcon, ClockIcon, ShareIcon, BookmarkIcon } from 'lucide-react';
export const ArticlePage = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const articleId = parseInt(id || '0');
  // Find the article by ID
  const article = newsData.find((item, index) => index === articleId);
  if (!article) {
    return <div className="container mx-auto px-4 py-12 text-center" data-id="element-295">
        <h2 className="text-2xl font-bold mb-4" data-id="element-296">Article not found</h2>
        <p className="mb-8" data-id="element-297">
          The article you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/" className="text-blue-600 hover:underline" data-id="element-298">
          Return to home page
        </Link>
      </div>;
  }
  // Get articles in the same category for the "Related Articles" section
  const relatedArticles = newsData.filter(item => item.category === article.category && item !== article).slice(0, 3);
  return <main className="flex-grow" data-id="element-299">
      {/* Article Header */}
      <div className="bg-white border-b border-gray-200 py-8" data-id="element-300">
        <div className="container mx-auto px-4 lg:px-6" data-id="element-301">
          <div className="max-w-3xl mx-auto" data-id="element-302">
            <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 text-xs font-medium rounded mb-4" data-id="element-303">
              {article.category?.toUpperCase()}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-304">
              {article.title}
            </h1>
            <p className="text-xl text-gray-600 mb-6" data-id="element-305">{article.excerpt}</p>
            <div className="flex items-center justify-between border-t border-b border-gray-200 py-4 mb-8" data-id="element-306">
              <div className="flex items-center" data-id="element-307">
                <div className="h-10 w-10 rounded-full bg-gray-300 mr-3" data-id="element-308"></div>
                <div data-id="element-309">
                  <p className="font-medium" data-id="element-310">{article.author}</p>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-311">
                    <CalendarIcon className="h-4 w-4 mr-1" data-id="element-312" />
                    <span className="mr-3" data-id="element-313">Published {article.date}</span>
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-314" />
                    <span data-id="element-315">8 min read</span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2" data-id="element-316">
                <button className="p-2 rounded-full hover:bg-gray-100" data-id="element-317">
                  <ShareIcon className="h-5 w-5 text-gray-500" data-id="element-318" />
                </button>
                <button className="p-2 rounded-full hover:bg-gray-100" data-id="element-319">
                  <BookmarkIcon className="h-5 w-5 text-gray-500" data-id="element-320" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Article Content */}
      <div className="container mx-auto px-4 lg:px-6 py-8" data-id="element-321">
        <div className="max-w-3xl mx-auto" data-id="element-322">
          <img src={article.imageUrl} alt={article.title} className="w-full h-[400px] object-cover rounded-lg mb-8" data-id="element-323" />
          <div className="prose max-w-none" data-id="element-324">
            {article.content ? <div dangerouslySetInnerHTML={{
            __html: article.content
          }} data-id="element-325" /> : <>
                <p className="mb-4" data-id="element-326">
                  In a historic moment for international climate policy, world
                  leaders from over 190 nations have reached an unprecedented
                  agreement to combat climate change at this week's global
                  summit. The landmark accord, which has been hailed as the most
                  significant environmental pact since the Paris Agreement,
                  commits participating countries to reducing carbon emissions
                  by a collective 50% before 2030.
                </p>
                <p className="mb-4" data-id="element-327">
                  "This is a defining moment in our fight against the climate
                  crisis," said United Nations Secretary-General at the closing
                  ceremony. "For the first time, we have a truly global
                  commitment that matches the scale of the challenge we face."
                </p>
                <p className="mb-4" data-id="element-328">
                  The agreement, which will be legally binding, includes several
                  key provisions:
                </p>
                <ul className="list-disc pl-5 mb-4" data-id="element-329">
                  <li className="mb-2" data-id="element-330">
                    Accelerated phase-out of coal power across all developed
                    nations by 2028
                  </li>
                  <li className="mb-2" data-id="element-331">
                    A $100 billion annual fund to help developing nations
                    transition to clean energy
                  </li>
                  <li className="mb-2" data-id="element-332">
                    Mandatory climate risk disclosure for all companies with
                    annual revenues exceeding $100 million
                  </li>
                  <li className="mb-2" data-id="element-333">
                    Creation of an international carbon pricing framework
                  </li>
                </ul>
                <p className="mb-4" data-id="element-334">
                  The breakthrough came after five days of intense negotiations,
                  during which several key holdout nations finally agreed to the
                  terms following concessions on implementation timelines and
                  financial support mechanisms.
                </p>
                <p className="mb-4" data-id="element-335">
                  Climate scientists have widely praised the agreement, with Dr.
                  Eleanor Chambers of the Global Climate Institute calling it
                  "the first international climate accord that truly has teeth."
                  She added, "If fully implemented, these measures could
                  actually keep us within the 1.5°C warming target."
                </p>
                <p className="mb-4" data-id="element-336">
                  However, some environmental groups have expressed concern
                  about enforcement mechanisms. "The targets are impressive, but
                  without robust compliance measures, we risk a repeat of
                  previous agreements where countries simply ignored their
                  commitments," said Greenpeace in a statement.
                </p>
                <p className="mb-4" data-id="element-337">
                  Market reaction to the announcement has been mixed, with
                  renewable energy stocks surging while shares in traditional
                  fossil fuel companies experienced significant declines. The
                  agreement is expected to accelerate the already rapid growth
                  in clean energy investment, which reached $366 billion
                  globally last year.
                </p>
                <p className="mb-6" data-id="element-338">
                  Implementation of the agreement will begin immediately, with
                  the first progress review scheduled for next year's follow-up
                  summit. Nations will be required to submit detailed transition
                  plans within six months.
                </p>
              </>}
          </div>
          <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-339">
            <h3 className="text-lg font-bold mb-4" data-id="element-340">Tags</h3>
            <div className="flex flex-wrap gap-2" data-id="element-341">
              <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm" data-id="element-342">
                Climate Change
              </span>
              <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm" data-id="element-343">
                Global Policy
              </span>
              <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm" data-id="element-344">
                Environment
              </span>
              <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm" data-id="element-345">
                Carbon Emissions
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Related Articles */}
      <div className="bg-gray-50 py-12" data-id="element-346">
        <div className="container mx-auto px-4 lg:px-6" data-id="element-347">
          <div className="max-w-5xl mx-auto" data-id="element-348">
            <h2 className="text-2xl font-bold mb-6" data-id="element-349">Related Articles</h2>
            <RelatedArticles articles={relatedArticles} data-id="element-350" />
          </div>
        </div>
      </div>
    </main>;
};